import { TypedDataDomain } from 'viem';

export type Hex = `0x${string}`;

export interface PunkBidOrderInput {
  numberOfUnits: bigint | number | string;
  pricePerUnit: bigint | number | string;
  auction: Hex;
}

export interface PunkBidTypedDataInput {
  chainId: number;
  verifyingContract: Hex;
  root: Hex;
  accountNonce: bigint | number | string;
  bidNonce: bigint | number | string;
  expiration: bigint | number | string;
  order: PunkBidOrderInput;
}

export interface PunkBidOrderMessage {
  numberOfUnits: number;
  pricePerUnit: bigint;
  auction: Hex;
}

export interface PunkBidMessage {
  order: PunkBidOrderMessage;
  accountNonce: bigint;
  bidNonce: bigint;
  expiration: bigint;
  root: Hex;
}

export interface PunkBidTypedData {
  domain: TypedDataDomain;
  types: typeof PUNK_BID_TYPES;
  primaryType: 'PunkBid';
  message: PunkBidMessage;
}

export interface ERC721BidTypedData {
  domain: TypedDataDomain;
  types: typeof ERC721_BID_TYPES;
  primaryType: 'ERC721Bid';
  message: PunkBidMessage;
}

export const PUNK_BID_TYPES = {
  Order: [
    { name: 'numberOfUnits', type: 'uint16' },
    { name: 'pricePerUnit', type: 'uint80' },
    { name: 'auction', type: 'address' },
  ],
  PunkBid: [
    { name: 'order', type: 'Order' },
    { name: 'accountNonce', type: 'uint256' },
    { name: 'bidNonce', type: 'uint256' },
    { name: 'expiration', type: 'uint256' },
    { name: 'root', type: 'bytes32' },
  ],
} as const;

// ERC721Bid types for V1 (same structure but different primary type name)
export const ERC721_BID_TYPES = {
  Order: [
    { name: 'numberOfUnits', type: 'uint16' },
    { name: 'pricePerUnit', type: 'uint80' },
    { name: 'auction', type: 'address' },
  ],
  ERC721Bid: [
    { name: 'order', type: 'Order' },
    { name: 'accountNonce', type: 'uint256' },
    { name: 'bidNonce', type: 'uint256' },
    { name: 'expiration', type: 'uint256' },
    { name: 'root', type: 'bytes32' },
  ],
} as const;

const HEX_32 = /^0x[0-9a-fA-F]{64}$/;
const MAX_UINT16 = BigInt(2 ** 16 - 1);

function toBigInt(value: bigint | number | string, field: string): bigint {
  if (typeof value === 'bigint') {
    return value;
  }

  if (typeof value === 'number') {
    if (!Number.isInteger(value)) {
      throw new Error(`${field} must be an integer`);
    }
    return BigInt(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      throw new Error(`${field} cannot be empty`);
    }
    return BigInt(trimmed);
  }

  throw new Error(`${field} must be bigint | number | string`);
}

function toUint16(value: bigint | number | string, field: string): number {
  const normalized = toBigInt(value, field);
  if (normalized < 0n || normalized > MAX_UINT16) {
    throw new Error(`${field} must fit within uint16`);
  }
  return Number(normalized);
}

function assertRoot(root: Hex): void {
  if (!HEX_32.test(root)) {
    throw new Error('root must be a 32-byte hex string');
  }
}

export function createPunkBidTypedData(input: PunkBidTypedDataInput): PunkBidTypedData {
  assertRoot(input.root);

  const domain: TypedDataDomain = {
    chainId: input.chainId,
    verifyingContract: input.verifyingContract,
  };

  const order: PunkBidOrderMessage = {
    numberOfUnits: toUint16(input.order.numberOfUnits, 'order.numberOfUnits'),
    pricePerUnit: toBigInt(input.order.pricePerUnit, 'order.pricePerUnit'),
    auction: input.order.auction,
  };

  const message: PunkBidMessage = {
    order,
    accountNonce: toBigInt(input.accountNonce, 'accountNonce'),
    bidNonce: toBigInt(input.bidNonce, 'bidNonce'),
    expiration: toBigInt(input.expiration, 'expiration'),
    root: input.root,
  };

  return {
    domain,
    types: PUNK_BID_TYPES,
    primaryType: 'PunkBid',
    message,
  };
}

export function createERC721BidTypedData(input: PunkBidTypedDataInput): ERC721BidTypedData {
  assertRoot(input.root);

  const domain: TypedDataDomain = {
    chainId: input.chainId,
    verifyingContract: input.verifyingContract,
  };

  const order: PunkBidOrderMessage = {
    numberOfUnits: toUint16(input.order.numberOfUnits, 'order.numberOfUnits'),
    pricePerUnit: toBigInt(input.order.pricePerUnit, 'order.pricePerUnit'),
    auction: input.order.auction,
  };

  const message: PunkBidMessage = {
    order,
    accountNonce: toBigInt(input.accountNonce, 'accountNonce'),
    bidNonce: toBigInt(input.bidNonce, 'bidNonce'),
    expiration: toBigInt(input.expiration, 'expiration'),
    root: input.root,
  };

  return {
    domain,
    types: ERC721_BID_TYPES,
    primaryType: 'ERC721Bid',
    message,
  };
}

